import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Heart, ArrowRight, CheckCircle2, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [pasangan, setPasangan] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pasangan.trim() || !identifier.trim() || !password) {
      toast.error("Lengkapi Pasangan, Username/Email, dan Password baru");
      return;
    }
    if (password.length < 4) { toast.error("Password baru minimal 4 karakter"); return; }
    if (password !== confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/public/reset-password`, { pasangan, identifier, new_password: password });
      setDone(true);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Username atau email tidak ditemukan");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] px-6 text-white">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center" data-testid="reset-done">
        <CheckCircle2 className="mx-auto h-14 w-14 text-green-400" />
        <h1 className="mt-4 font-heading text-4xl font-light">Password Diubah!</h1>
        <p className="mt-3 text-white/70">Password kamu berhasil diperbarui. Silakan masuk dengan password baru.</p>
        <Link to="/login"><Button className="mt-6 rounded-full bg-white text-black hover:bg-white/90" data-testid="reset-go-login">Ke Halaman Login</Button></Link>
      </motion.div>
    </div>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] px-6 py-12 text-white">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
        data-testid="reset-form"
      >
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50">
          <KeyRound className="h-3.5 w-3.5" /> Reset Password
        </p>
        <h2 className="mt-2 font-heading text-3xl font-light">Lupa password?</h2>
        <p className="mt-2 text-sm text-white/60">Masukkan Kode Pasangan & Username/Email kamu untuk membuat password baru.</p>

        <div className="mt-8 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Pasangan</Label>
            <Input data-testid="reset-pasangan" value={pasangan} onChange={(e) => setPasangan(e.target.value)} placeholder="Kode pasangan (mis. KH001)" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Username / Email</Label>
            <Input data-testid="reset-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="username atau email" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Password Baru</Label>
            <div className="relative mt-2">
              <Input data-testid="reset-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password baru" className="border-white/15 bg-white/5 pr-10 text-white placeholder:text-white/30" />
              <button type="button" data-testid="reset-toggle-pw" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Konfirmasi Password Baru</Label>
            <Input data-testid="reset-confirm" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="ulangi password baru" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
          </div>
        </div>

        <Button data-testid="reset-submit" type="submit" disabled={loading} className="group mt-8 h-12 w-full rounded-full bg-white text-black hover:bg-white/90">
          {loading ? "Memproses..." : (<span className="flex items-center gap-2">Reset Password <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>)}
        </Button>

        <p className="mt-6 text-center text-xs text-white/40">
          Ingat password kamu? <Link to="/login" className="underline hover:text-white">Masuk di sini</Link>
        </p>
      </motion.form>
    </div>
  );
}
